import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LAYER_META } from "@/components/map/BusinessLayers";

// Bottom-left overlay of Business Discovery layer chips (Restaurants, Bike
// Shops, Coffee, …, Heatmap) + the bike-shop sub-filter. Positioned over a
// map inside a `relative` container. Shared by Bike Paths, Zones and Trail Lab.
export function BusinessLayerToggles({ layers, setLayers, heatOn, setHeatOn, bikeFilter, setBikeFilter, buffer, setBuffer, bizCounts, bizLoading, bizStatus, bizSource, unsyncedZones = 0, hideBikeFilter = false, restaurantsOnly = true }) {
  const [showAll, setShowAll] = useState(!restaurantsOnly);
  const metaEntries = Object.entries(LAYER_META).filter(([k]) => showAll || k === "restaurant");
  return (
    <div className="absolute bottom-3 left-3 z-[1000] flex flex-col gap-2 max-w-[calc(100%-1.5rem)]" data-testid="business-layer-toggles">
      <div className="flex flex-wrap items-center gap-1.5">
        {metaEntries.map(([k, m]) => (
          <button
            key={k}
            type="button"
            onClick={() => setLayers((l) => ({ ...l, [k]: !l[k] }))}
            data-testid={`biz-toggle-${k}`}
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] backdrop-blur transition-colors ${layers[k] ? "bg-card/95 border-border text-foreground" : "bg-card/60 border-border/50 text-muted-foreground"}`}
          >
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color, opacity: layers[k] ? 1 : 0.35 }} />
            {m.label}<span className="opacity-60">{bizCounts[k] || 0}</span>
          </button>
        ))}
        {restaurantsOnly && (
          <button type="button" onClick={() => {
            if (showAll) setLayers((l) => Object.fromEntries(Object.keys(l).map((k) => [k, k === "restaurant" ? l[k] : false])));
            setShowAll((v) => !v);
          }} data-testid="biz-toggle-more" className="rounded-full border border-dashed border-border bg-card/80 px-2.5 py-1 text-[11px] text-muted-foreground backdrop-blur hover:text-foreground transition-colors">
            {showAll ? "Restaurants only" : "More categories"}
          </button>
        )}
        <button type="button" onClick={() => setHeatOn((v) => !v)} data-testid="biz-toggle-heat"
          className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] backdrop-blur transition-colors ${heatOn ? "bg-card/95 border-border text-foreground" : "bg-card/60 border-border/50 text-muted-foreground"}`}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#22d3ee,#facc15,#ef4444)", opacity: heatOn ? 1 : 0.4 }} />
          Heatmap
        </button>
        {setBuffer && (
          <div className="flex items-center gap-1.5 rounded-full border border-border bg-card/95 backdrop-blur pl-2.5 pr-1 py-0.5" data-testid="corridor-width">
            <span className="text-[11px] text-muted-foreground whitespace-nowrap">Corridor</span>
            <Select value={String(buffer)} onValueChange={(v) => setBuffer(Number(v))}>
              <SelectTrigger className="h-6 text-[11px] w-24 border-0 bg-transparent px-1 focus:ring-0" data-testid="corridor-width-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0.25">¼ mile</SelectItem>
                <SelectItem value="0.5">½ mile</SelectItem>
                <SelectItem value="1">1 mile</SelectItem>
                <SelectItem value="2">2 miles</SelectItem>
                <SelectItem value="3">3 miles</SelectItem>
                <SelectItem value="5">5 miles</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {layers.bike_shop && !hideBikeFilter && (
        <div className="flex items-center gap-1.5" data-testid="bike-shop-filter">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: LAYER_META.bike_shop.color }} />
          <Select value={bikeFilter} onValueChange={setBikeFilter}>
            <SelectTrigger className="h-7 text-[11px] w-40 bg-card/95 backdrop-blur" data-testid="bike-shop-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All bike shops</SelectItem>
              <SelectItem value="partners">Partners only</SelectItem>
              <SelectItem value="prospects">Prospects only</SelectItem>
              <SelectItem value="non_contacted">Non-contacted</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {bizLoading && (
        <div className="w-fit flex items-center gap-1.5 rounded-md border border-border bg-card/90 px-2 py-1 text-[11px]" data-testid="biz-loading">
          <Loader2 className="w-3 h-3 animate-spin" />Loading nearby businesses…
        </div>
      )}
      {!bizLoading && bizSource === "saved" && (
        <div className="w-fit rounded-md border border-border bg-card/90 px-2 py-1 text-[11px] text-muted-foreground" data-testid="biz-saved-note">
          Showing saved prospects{unsyncedZones > 0 ? ` · ${unsyncedZones} zone${unsyncedZones === 1 ? " hasn't" : "s haven't"} been synced yet` : ""}
        </div>
      )}
      {!bizLoading && bizStatus === "unavailable" && (
        <div className="w-fit rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-1 text-[11px] text-amber-300" data-testid="biz-unavailable">
          Business data temporarily unavailable
        </div>
      )}
    </div>
  );
}
